import moment from 'moment';
import {
  LOCAL_STORAGE_KEYS,
  RECORD_BINDER_STATUS,
} from '../../constants/AppConstants';
import { PBStorage } from '../../services/storageService/GlobalData';
import RecordBinderHelper from '../../helpers/RecordBinderHelper';

class RecordBinderManager {
  async saveRecordBinders(recordBindersToSave = []) {
    try {
      let existingRecordBinders =
        (await PBStorage.get(LOCAL_STORAGE_KEYS.RECORD_BINDERS)) || [];

      for (let i in recordBindersToSave) {
        let recordBinder = recordBindersToSave[i];
        let recordIndex = existingRecordBinders.findIndex(
          (q) => q.id == recordBinder.id,
        );

        if (recordIndex != -1) {
          existingRecordBinders[recordIndex] = recordBinder;
        } else {
          existingRecordBinders.push(recordBinder);
        }
      }

      await PBStorage.set(
        LOCAL_STORAGE_KEYS.RECORD_BINDERS,
        existingRecordBinders,
      );
      return true;
    } catch (e) {
      console.log('saveRecordBinders failed', e);
      return false;
    }
  }

  async getRecordBinders(query = '', filter = {}) {
    try {
      let recordBinders =
        (await PBStorage.get(LOCAL_STORAGE_KEYS.RECORD_BINDERS)) || [];

      if (query && query.trim() !== '') {
        const search = query.toLowerCase();
        recordBinders = recordBinders.filter((item) => {
          const name = item.record_name?.toLowerCase() || '';
          const instructions = item.instructions?.toLowerCase() || '';
          return name.includes(search) || instructions.includes(search);
        });
      }

      const hasStatusFilter =
        filter && filter.status !== null && filter.status !== undefined;
      const hasDateFilter = filter && (filter.startDate || filter.endDate);

      if (hasStatusFilter || hasDateFilter) {
        const { status, startDate, endDate } = filter;

        const startDateDayStart = startDate
          ? moment(startDate).startOf('day').utc().toDate()
          : null;
        const startDateDayEnd = startDate
          ? moment(startDate).endOf('day').utc().toDate()
          : null;
        const endDateDayStart = endDate
          ? moment(endDate).startOf('day').utc().toDate()
          : null;
        const endDateDayEnd = endDate
          ? moment(endDate).endOf('day').utc().toDate()
          : null;

        recordBinders = recordBinders.filter((item) => {
          let statusFound = true;

          if (hasStatusFilter) {
            const itemStatus = Number(item.status);
            const filterStatus = Number(status);

            if (filterStatus === RECORD_BINDER_STATUS.ACTIVE) {
              //user applied active filter
              statusFound = [
                RECORD_BINDER_STATUS.ACTIVE,
                RECORD_BINDER_STATUS.EXPIRED,
              ].includes(itemStatus); //active, Expired
            } else if (filterStatus === RECORD_BINDER_STATUS.ARCHIVED) {
              //user applied archived filter
              statusFound = [
                RECORD_BINDER_STATUS.ARCHIVED,
                RECORD_BINDER_STATUS.EXPIRED,
              ].includes(itemStatus); //Archived, Expired
            } else {
              //user applied Expired or Bin filter
              statusFound = [filterStatus].includes(itemStatus);
            }

            //In case of Active and Expired fetch items with Admin status of Active or Expired as well
            if (filterStatus !== RECORD_BINDER_STATUS.EXPIRED) {
              statusFound = item.admin_status === filterStatus;
            }
          }

          if (hasDateFilter) {
            const expiryDate = item.expiry_date;
            if (!expiryDate) return false;

            const recordDate = new Date(expiryDate);

            if (startDate && endDate) {
              return (
                recordDate >= startDateDayStart &&
                recordDate <= endDateDayEnd &&
                statusFound
              );
            } else if (startDate) {
              return (
                recordDate >= startDateDayStart &&
                recordDate <= startDateDayEnd &&
                statusFound
              );
            } else if (endDate) {
              return (
                recordDate >= endDateDayStart &&
                recordDate <= endDateDayEnd &&
                statusFound
              );
            }
          }

          return statusFound;
        });
      }

      recordBinders = recordBinders.sort((first, second) => {
        // If filter.sort is true, sort alphabetically by record_name
        if (filter && filter.sort) {
          const firstName = (first.record_name || '').toLowerCase();
          const secondName = (second.record_name || '').toLowerCase();
          return firstName.localeCompare(secondName);
        }

        // Otherwise, sort by expiry_date (existing logic)
        const firstExpiry =
          first && first.expiry_date ? new Date(first.expiry_date) : null;
        const secondExpiry =
          second && second.expiry_date ? new Date(second.expiry_date) : null;

        if (!firstExpiry && !secondExpiry) {
          return 0;
        }
        if (!firstExpiry) {
          return 1;
        }
        if (!secondExpiry) {
          return -1;
        }
        return firstExpiry.getTime() - secondExpiry.getTime();
      });

      return recordBinders;
    } catch (e) {
      console.log('getRecordBinders failed', e);
      return [];
    }
  }

  async deleteRecordBindersByIds(ids = []) {
    if (!Array.isArray(ids) || ids.length === 0) return false;
    try {
      let existingRecordBinders =
        (await PBStorage.get(LOCAL_STORAGE_KEYS.RECORD_BINDERS)) || [];
      existingRecordBinders = existingRecordBinders.filter(
        (binder) => !ids.includes(binder.id),
      );
      await PBStorage.set(
        LOCAL_STORAGE_KEYS.RECORD_BINDERS,
        existingRecordBinders,
      );
      return true;
    } catch (e) {
      console.log('deleteRecordBindersByIds failed', e);
      return false;
    }
  }
  async saveRecordHistory(recordId, histories = []) {
    try {
      let allHistories =
        (await PBStorage.get(LOCAL_STORAGE_KEYS.RECORD_HISTORY)) || {};
      allHistories[recordId] = histories;
      await PBStorage.set(LOCAL_STORAGE_KEYS.RECORD_HISTORY, allHistories);
      return true;
    } catch (e) {
      console.log('saveRecordHistory failed', e);
      return false;
    }
  }

  async getRecordHistory(recordId, filter = {}) {
    try {
      let allHistories =
        (await PBStorage.get(LOCAL_STORAGE_KEYS.RECORD_HISTORY)) || {};
      let histories = allHistories[recordId] || [];

      const { startDate, endDate } = filter || {};

      const startDateDayStart =
        startDate && moment(startDate).startOf('day').utc().toDate();
      const startDateDayEnd =
        startDate && moment(startDate).endOf('day').utc().toDate();
      const endDateDayStart =
        endDate && moment(endDate).startOf('day').utc().toDate();
      const endDateDayEnd =
        endDate && moment(endDate).endOf('day').utc().toDate();

      if (startDate || endDate) {
        histories = histories.filter((item) => {
          const expiryDate = item.expiresOn || item.expiry_date;
          if (!expiryDate) {
            return false;
          }

          const recordDate = new Date(expiryDate);

          if (startDate && endDate) {
            return (
              recordDate >= startDateDayStart && recordDate <= endDateDayEnd
            );
          } else if (startDate) {
            return (
              recordDate >= startDateDayStart && recordDate <= startDateDayEnd
            );
          } else if (endDate) {
            return recordDate >= endDateDayStart && recordDate <= endDateDayEnd;
          }

          return true;
        });
      }

      return RecordBinderHelper.sortRecordHistoryByDate(histories);
    } catch (e) {
      console.log('getRecordHistory failed', e);
      return [];
    }
  }
}

export default new RecordBinderManager();
